<script>
	import { triggerResize } from "../../../utils";
	import { AppContent } from "@smui/drawer";
	import Button, { Label, Icon } from "@smui/button";
	import LayoutGrid, { Cell } from "@smui/layout-grid";
	import { Icon as CommonIcon } from "@smui/common";
	import Card, { Content } from "@smui/card";

	import EditOption from "./EditOption.svelte";
	import InternalBadge from "./InternalBadge.svelte";

	import {
		setupComplete,
		embedInfo,
		selectedOrganizationObj,
		organizationProjectSettings,
		debug,
	} from "../../../stores";
	import { optionsToObject } from "../../../utils";

	export let graphic;
	export let createInstance;

	$: options = graphic.graphic_options;
	$: optionsStringified = JSON.stringify(optionsToObject(options));

	$: fontThemeString =
		($organizationProjectSettings && $organizationProjectSettings.font_theme.original) ||
		$selectedOrganizationObj.font_theme.original;

	$: colorThemeString = ($organizationProjectSettings && $organizationProjectSettings.color_theme.original) || "";

	// if setup complete and graphic or instance changes, trigger resize
	$: if ($setupComplete) graphic, options, fontThemeString, colorThemeString, triggerResize();

	$: if ($debug) console.log("GRAPHIC", graphic);
	$: console.log($selectedOrganizationObj, $organizationProjectSettings);

	
	let colorScheme = 'light';
</script>

<main>
	{#if graphic}
		<div class="container graphic">
			<h4>
				{graphic.meta_title}
				{#if !graphic.ready}
					<div class="not-ready">
						<CommonIcon class="material-icons visibility_off">visibility_off</CommonIcon> Not published
					</div>
				{/if}
				{#if graphic.internal_only}
					<InternalBadge />
				{/if}
				<div class="button-container">
					<Button on:click={() => createInstance(graphic, options)} color="secondary" variant="raised">
						<Icon class="material-icons button-icon">add</Icon>
						<Label>Use graphic</Label>
					</Button>
				</div>
      </h4>
      {#if graphic.internal_only}
        <p class="internal-note">
          Internal use only — this graphic can be configured and previewed, but produces no embed code.
        </p>
      {/if}
      {#if graphic.meta_description}
        <div class="description">
          {@html graphic.meta_description}
        </div>
      {/if}
			<Card style="background-color: {colorScheme == "dark" ? ($selectedOrganizationObj.color_background_dark || "#1a1a1a") : ($selectedOrganizationObj.color_background || "#ffffff")}">
				<iframe
					class={$embedInfo.resize_iframe_class}
					src="{graphic.embed_url_preview}?options={encodeURIComponent(optionsStringified)}&fonttheme={encodeURIComponent(fontThemeString)}&colortheme={encodeURIComponent(colorThemeString)}&force_color_scheme={colorScheme}"
					frameborder="0"
					scrolling="no"
					title=""
					on:load={() => {
						triggerResize();
					}}
				/>
			</Card>
			
			{#if graphic.supports_darkmode && $selectedOrganizationObj.darkmode_enabled}
				<div class="darkmode-toggle">
					<span>Preview</span>
					<Button on:click={() => (colorScheme = 'light')} color="primary" variant={colorScheme === 'light' ? 'unelevated' : 'outlined'}>
						Light
					</Button>
					<Button on:click={() => (colorScheme = 'dark')} color="primary" variant={colorScheme === 'dark' ? 'unelevated' : 'outlined'}>
						Dark
					</Button>
				</div>
			{/if}
			

			{#if options.length > 0}
				<h5>Options</h5>
				<div class="options-container">
					{#key graphic.id}
						{#each options as o}
							<EditOption bind:selected={o.selected} bind:modified={o.modified} {...o} />
						{/each}
					{/key}
				</div>
			{/if}

			{#if graphic.datasets?.length > 0}
				<h5>Datasets</h5>
				<div>
					{#each graphic.datasets as dataset}
						<div class="dataset-item">
							<Button href={dataset.url} variant="raised" target="_blank" download>
								<Icon class="material-icons button-icon">download</Icon>
								<Label>{dataset.name || dataset.dataset_id}</Label>
							  </Button>
						</div>
					{/each}
				</div>
			{/if}
		</div>
	{/if}
</main>

<style lang="scss">
	main {
		overflow: scroll;
		padding: 16px;
		height: 100%;
		box-sizing: border-box;
		display: flex;
		justify-content: center;
		width: 100%;
	}

	.container {
		flex-grow: 1;
		max-width: 800px;
		padding-bottom: 250px;
		height: fit-content;
	}

	.graphic {
		:global(.mdc-card) {
			padding: 20px;
		}

		h4 {
			display: flex;
			align-items: center;
			gap: 15px;

			:global(.mdc-button__label) {
				white-space: nowrap;
			}
		}
	}

	.not-ready {
		display: inline;
		white-space: nowrap;
		color: $primary;

		:global(.visibility_off) {
			font-size: 1em;
			vertical-align: text-top;
		}
	}

	.internal-note {
		margin-top: 0;
		color: #555;
	}

	.button-container {
		margin-left: auto;
		align-self: center;
	}

	h5 {
		margin-bottom: 20px;
		margin-top: 70px;
	}

	.options-container {
		display: flex;
		flex-direction: column;
		gap: 30px;
	}

	* :global(.content) {
		flex: auto;
		overflow: auto;
		position: relative;
		flex-grow: 1;
	}

	* :global(.mdc-layout-grid__inner) {
		height: 100%;
	}

	iframe {
		width: 100%;
		/*min-height: 150px;*/
  }

  .description {
    margin: 30px 0 50px;
  }

  .darkmode-toggle {
	margin-top: 15px;
	display:flex;
	align-items: center;
	justify-content: end;
	gap: 5px;
  }

  .dataset-item {
	margin-bottom: 10px;
  }


</style>
